import React, { useEffect, useState } from "react";

import moment from "moment";

import NutritionHeader from "./components/NutritionHeader";
import NutritionPlans from "./components/NutritionPlans";
import NutritionHistory from "./components/NutritionHistory";
import NutritionAdherence from "./components/NutritionAdherence";
import NutritionSummary from "./components/NutritionSummary";

import {
  getActiveDayStats,
  getAverageStats,
  getFilteredStats,
} from "../../../utils/nutrition-helpers";
import { AthleteLinks } from "../../../parts/athlete-links";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";

const Nutrition = ({ locals }) => {
  const {
    activeExercises,
    activeUser,
    currentNutritionPlan,
    dailyStats,
    nutritionPlans,
    query,
    comesFromBodyComp,
    athlete,
    pathname,
  } = locals;
  const startingCalendarDay = activeUser.startingCalendarDay || 0;
  const { tabView } = query;
  const [activeDayUtc, setActiveDayUtc] = useState(query.utcDate);
  const InitialDateFrom = currentNutritionPlan
    ? moment(String(currentNutritionPlan.utcStartDate)).toDate()
    : moment(String(activeDayUtc)).subtract(7, "days").toDate();
  const InitialDateTo = currentNutritionPlan
    ? moment(String(currentNutritionPlan.utcEndDate)).toDate()
    : moment(String(activeDayUtc)).toDate();

  const [selectedPlan, setSelectedPlan] = useState(
    currentNutritionPlan || null
  );

  const initialViewState = tabView ? tabView : "progress";
  const [viewMode, setViewMode] = useState(initialViewState);

  // Change to single object when webpack changes
  const [dateFrom, setDateFrom] = useState(InitialDateFrom);
  const [dateTo, setDateTo] = useState(InitialDateTo);

  // Initial stats for when the user first enters the page
  const initialStats = getFilteredStats(
    dateFrom,
    dateTo,
    dailyStats,
    activeExercises
  );

  // Initial average stats for when the user first enters the page
  const initialAverageStats = getAverageStats(
    dateFrom,
    dateTo,
    startingCalendarDay,
    dailyStats
  );

  const [stats, setStats] = useState(initialStats);
  const [averageStats, setAverageStats] = useState(initialAverageStats);

  const initialActiveDayStats = getActiveDayStats(
    activeDayUtc,
    stats,
    averageStats
  );
  const [activeDayStats, setActiveDayStats] = useState(initialActiveDayStats);

  const [showAveragesOnly, setShowAveragesOnly] = useState(false);
  const [isActiveDayPeriod, setIsActiveDayPeriod] = useState(false);

  // Gets the new stats when the user changes the date
  useEffect(() => {
    const newStats = getFilteredStats(
      dateFrom,
      dateTo,
      dailyStats,
      activeExercises
    );
    const newAverages = getAverageStats(
      dateFrom,
      dateTo,
      startingCalendarDay,
      dailyStats
    );

    setStats(newStats);
    setAverageStats(newAverages);
  }, [dateFrom, dateTo]);

  // or if the user changes the client
  useEffect(() => {
    const newStats = getFilteredStats(
      dateFrom,
      dateTo,
      dailyStats,
      activeExercises
    );
    const newAverages = getAverageStats(
      dateFrom,
      dateTo,
      startingCalendarDay,
      dailyStats
    );

    setStats(newStats);
    setAverageStats(newAverages);
    setActiveDayStats(undefined);
  }, [locals]);

  // Changes the date, when the webpack includes the spread operator change to single date object to use spread operator
  const handleDateChange = (date, field) => {
    if (field === "dateTo") {
      setDateTo(date);
    } else {
      setDateFrom(date);
    }
  };

  // Changes the plan in the selection
  // The conditional is for the empty select option
  const handleSelectedPlanChange = (value) => {
    // const { value } = e.target;

    if (value && value.length) {
      const selectedPlan = nutritionPlans.find(({ _id }) => _id === value);
      const dateFrom = moment(String(selectedPlan.utcStartDate)).toDate();
      const dateTo = moment(String(selectedPlan.utcEndDate)).toDate();
      setDateFrom(dateFrom);
      setDateTo(dateTo);
      setSelectedPlan(selectedPlan);
    }
  };

  const handleViewModeChange = (viewMode) => {
    setViewMode(viewMode);
  };

  // Gets the selected day stats when the user clicks on the table
  // Calls the getActiveDayStats helper
  const handleActiveDayChange = (activeDayUtc, isPeriod) => {
    const activeDayStats = getActiveDayStats(
      activeDayUtc,
      stats,
      averageStats,
      isPeriod
    );

    setActiveDayStats(activeDayStats);
    setActiveDayUtc(activeDayUtc);
    setIsActiveDayPeriod(isPeriod);
  };

  const handleShowAveragesOnly = (e) => {
    e.persist();
    const { checked } = e.target;
    setShowAveragesOnly(checked);
  };

  return (
    <main role="nutrition-wrapper" className="Nutrition">
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athlete}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(athlete?._id)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>
      <NutritionHeader
        viewMode={viewMode}
        handleViewModeChange={handleViewModeChange}
        comesFromBodyComp={comesFromBodyComp}
      />
      {viewMode === "progress" ? (
        <div className="Nutrition-Progress">
          <NutritionHistory
            activeDayUtc={activeDayUtc}
            dateFrom={dateFrom}
            dateTo={dateTo}
            stats={stats}
            averageStats={averageStats}
            nutritionPlans={nutritionPlans}
            selectedPlan={selectedPlan}
            handleDateChange={handleDateChange}
            handleActiveDayChange={handleActiveDayChange}
            handleSelectedPlanChange={handleSelectedPlanChange}
            activeUser={activeUser}
            isActiveDayPeriod={isActiveDayPeriod}
            showAveragesOnly={showAveragesOnly}
            handleShowAveragesOnly={handleShowAveragesOnly}
            activeExercises={activeExercises}
          />

          <div className="history-summary">
            <NutritionAdherence
              dateFrom={dateFrom}
              dateTo={dateTo}
              stats={stats}
              nutritionPlans={nutritionPlans}
              averageStats={averageStats}
              activeExercises={activeExercises}
            />
            <NutritionSummary
              activeDayUtc={activeDayUtc}
              dayStats={activeDayStats}
              dateFrom={dateFrom}
              dateTo={dateTo}
              stats={stats}
              withToggle
              nutritionPlans={nutritionPlans}
              activeExercises={activeExercises}
            />
          </div>
        </div>
      ) : (
        <NutritionPlans
          nutritionPlans={nutritionPlans}
          activeUser={activeUser}
          dailyStats={dailyStats}
        />
      )}
    </main>
  );
};

export default Nutrition;
